import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'; // Import HttpParams
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {environment} from "../../environments/environment";
import {ProfitAndLoss} from "./reports/profit-and-loss/profit-and-loss.model";
import {GeneralLedger} from "./ledgers/general-ledger/general-ledger.model";
import { APIListWrapper } from '../models/common/api-list-wrapper.model';
import { GeneralLedgerPeriodSummary } from './ledgers/general-ledger/general-ledger-period-summary.model';
import {PartnerLedger} from "./ledgers/partner-ledger/partner-ledger.model";
import {GeneralLedgerSummary} from "./ledgers/general-ledger/general-ledger-summary.model";
import {PartnerLedgerSummary} from "./ledgers/partner-ledger/partner-ledger-summary.model";
import {BalanceSheet} from "./reports/balance-sheet/balance-sheet.model";
import {TrialBalance} from "./reports/trial-balance/trial-balance.model";
import {TaxReport} from "./reports/tax-report/tax-report.model";
import {AgedReceivable} from "./reports/aged-receivable/aged-receivable.model";
import { SalesRegisterFilter, SalesRegisterPageResponse } from './reports/sales-register/sales-register.model';
import {EmployeeSalaryPayable} from "./ledgers/salary-ledger/employee-salary-payable.model";
import { Page } from '../models/page.model';

// Placeholder interfaces - define these based on your actual Java view classes
// It's good practice to have these in a separate file, e.g., 'report-types.ts'
export interface AgedPartnerBalance {
    [key: string]: any;
}
export interface AgedPayable {
    owningCompanyId: number;
    currencyCode: string;
    partnerId: number;
    partnerType: string;
    vendorName: string;
    entryNumber: string;
    entryDate: Date;
    reference: string;
    transactionAmount: number;
    daysOutstanding: number;
    agingBucket: string;

    // Legacy aggregate fields returned by the non-v2 endpoint.
    over90Days?: number;
    between60And90Days?: number;
    between30And60Days?: number;
    under30Days?: number;
    totalBalance?: number;
}
export interface CashBook {
    [key: string]: any;
}
export interface DayBook {
    [key: string]: any;
}
export interface ReportDiagnostic {
    code: string;
    severity: 'INFO' | 'WARN' | 'ERROR' | string;
    message: string;
    referenceId?: number | null;
    referenceNumber?: string | null;
    amount?: number | null;
}
export interface StandardReportResponse {
    reportCode: string;
    title: string;
    rows: Record<string, unknown>[];
    summary: Record<string, unknown>;
}
export interface LedgerReportSummary {
    openingBalance: number;
    openingDebitBalance?: number;
    openingCreditBalance?: number;
    totalDebits: number;
    totalCredits: number;
    closingBalance: number;
    closingDebitBalance?: number;
    closingCreditBalance?: number;
    unreconciledOpenBalance?: number;
    currencyCode?: string;
    recordCount?: number;
}
export interface LedgerPageResponse<T> {
    content: T[];
    totalElements: number;
    page: number;
    size: number;
    summary: LedgerReportSummary;
    diagnostics: ReportDiagnostic[];
}
export interface ReportResponse<T> {
    filters: Record<string, any>;
    summary: Record<string, any>;
    rows: T[];
    diagnostics: ReportDiagnostic[];
}
export interface FinancialReportPrintRequest {
    startDate?: Date | number | string | null;
    endDate?: Date | number | string | null;
    selectedPeriodType?: string | null;
    logisticsProjectId?: number | null;
    partnerId?: number | null;
    partnerType?: string | null;
    intervalDays?: number | null;
    intervalCount?: number | null;
    agingBasis?: string | null;
    salesRegister?: SalesRegisterFilter | null;
}

@Injectable({
    providedIn: 'root' // Makes the service a singleton available throughout the app
})
export class AccountingReportsService {

    private apiBaseUrl = `${environment.restApiUrl}accounting/reports`; // Or your full base URL

    constructor(private http: HttpClient) { }

    /**
     * Retrieves the general ledger for a specific company.
     * @param owningCompanyId ID of the owning company
     */
    public getGeneralLedger(owningCompanyId: number, page: number, size: number, filters: any): Observable<Page<GeneralLedger>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString());

        // Using POST to send complex filter object in the body
        // TODO: Update the backend endpoint to match this new structure, e.g., '/general-ledger/paged/{id}'
        return this.http.post<Page<GeneralLedger>>(`${this.apiBaseUrl}/general-ledger/paged/${owningCompanyId}`, filters, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    public getGeneralLedgerV2(owningCompanyId: number, page: number, size: number, filters: any): Observable<LedgerPageResponse<GeneralLedger>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString());

        return this.http.post<LedgerPageResponse<GeneralLedger>>(`${this.apiBaseUrl}/v2/general-ledger/${owningCompanyId}`, filters, { params })
            .pipe(catchError(this.handleError));
    }

    public getGeneralLedgerAllV2(owningCompanyId: number, filters: any): Observable<LedgerPageResponse<GeneralLedger>> {
        return this.http.post<LedgerPageResponse<GeneralLedger>>(`${this.apiBaseUrl}/v2/general-ledger/all/${owningCompanyId}`, filters)
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the general ledger summary for a specific company.
     * @param owningCompanyId ID of the owning company
     * @param filters
     */
    public getGeneralLedgerSummary(owningCompanyId: number, filters: any): Observable<GeneralLedgerPeriodSummary> {
        // Using POST to send complex filter object in the body
        // TODO: Update the backend endpoint to match this new structure, e.g., '/general-ledger-summary/filtered/{id}'
        return this.http.post<GeneralLedgerPeriodSummary>(`${this.apiBaseUrl}/general-ledger-summary/filtered/${owningCompanyId}`, filters)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Retrieves all general ledger entries that match the filters, without pagination.
     * Used for exporting data.
     * @param owningCompanyId ID of the owning company
     * @param filters The filter criteria
     */
    public getGeneralLedgerAll(owningCompanyId: number, filters: any): Observable<GeneralLedger[]> {
        // TODO: Update the backend endpoint to match this new structure, e.g., '/general-ledger/all/filtered/{id}'
        return this.http.post<GeneralLedger[]>(`${this.apiBaseUrl}/general-ledger/all/filtered/${owningCompanyId}`, filters)
            .pipe(
                catchError(this.handleError)
            );
    }


     /**
     * Retrieves the partner ledger for a specific company.
     * @param owningCompanyId ID of the owning company
     */
    public getPartnerLedger(owningCompanyId: number): Observable<PartnerLedger[]> {
        return this.http.get<PartnerLedger[]>(`${this.apiBaseUrl}/partner-ledger/${owningCompanyId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    public getPartnerLedgerV2(owningCompanyId: number, page: number, size: number, filters: any): Observable<LedgerPageResponse<PartnerLedger>> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString());

        return this.http.post<LedgerPageResponse<PartnerLedger>>(`${this.apiBaseUrl}/v2/partner-ledger/${owningCompanyId}`, filters, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the partner ledger summary for a specific company.
     * @param owningCompanyId ID of the owning company
     */
    public getPartnerLedgerSummary(owningCompanyId: number): Observable<PartnerLedgerSummary[]> {
        return this.http.get<PartnerLedgerSummary[]>(`${this.apiBaseUrl}/partner-ledger-summary/${owningCompanyId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    public getSalesRegisterV2(
        owningCompanyId: number,
        page: number,
        size: number,
        filters: SalesRegisterFilter
    ): Observable<SalesRegisterPageResponse> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString());
        return this.http.post<SalesRegisterPageResponse>(`${this.apiBaseUrl}/v2/sales-register/${owningCompanyId}`, filters, { params })
            .pipe(catchError(this.handleError));
    }

    public getSalesRegisterAllV2(
        owningCompanyId: number,
        filters: SalesRegisterFilter
    ): Observable<SalesRegisterPageResponse> {
        return this.http.post<SalesRegisterPageResponse>(`${this.apiBaseUrl}/v2/sales-register/all/${owningCompanyId}`, filters)
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the balance sheet for a specific company.
     * @param owningCompanyId ID of the owning company
     * @param selectedPeriodType
     * @param startDateNumber
     * @param endDateNumber
     */
    public getBalanceSheet(owningCompanyId: number,
                           selectedPeriodType: string, // e.g., 'yearly', 'quarterly', 'monthly'
                           startDateNumber?: number | null, // Changed from Long to number, made optional
                           endDateNumber?: number | null   // Changed from Long to number, made optional
    ): Observable<BalanceSheet[]> {
        let params = new HttpParams();
        params = params.append('selectedPeriodType', selectedPeriodType);
        if (startDateNumber != null) { // Check for null or undefined
            params = params.append('startDateNumber', startDateNumber.toString());
        }
        if (endDateNumber != null) { // Check for null or undefined
            params = params.append('endDateNumber', endDateNumber.toString());
        }

        return this.http.get<BalanceSheet[]>(`${this.apiBaseUrl}/balance-sheet/${owningCompanyId}`, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    public getBalanceSheetV2(owningCompanyId: number,
                             selectedPeriodType: string,
                             startDateNumber?: number | null,
                             endDateNumber?: number | null,
                             logisticsProjectId?: number | null): Observable<ReportResponse<BalanceSheet>> {
        let params = new HttpParams().append('selectedPeriodType', selectedPeriodType);
        if (startDateNumber != null) params = params.append('startDateNumber', startDateNumber.toString());
        if (endDateNumber != null) params = params.append('endDateNumber', endDateNumber.toString());
        if (logisticsProjectId != null) params = params.append('logisticsProjectId', logisticsProjectId.toString());
        return this.http.get<ReportResponse<BalanceSheet>>(`${this.apiBaseUrl}/v2/balance-sheet/${owningCompanyId}`, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the profit and loss statement for a specific company.
     * @param owningCompanyId ID of the owning company
     * @param startDateNumber Optional start date as a number (timestamp)
     * @param endDateNumber Optional end date as a number (timestamp)
     * @param selectedPeriodType
     */
    public getProfitAndLoss(
        owningCompanyId: number,
        selectedPeriodType: string, // e.g., 'yearly', 'quarterly', 'monthly'
        startDateNumber?: number | null, // Changed from Long to number, made optional
        endDateNumber?: number | null,  // Changed from Long to number, made optional
    ): Observable<ProfitAndLoss[]> {
        let params = new HttpParams();
        params = params.append('selectedPeriodType', selectedPeriodType);
        if (startDateNumber != null) { // Check for null or undefined
            params = params.append('startDateNumber', startDateNumber.toString());
        }
        if (endDateNumber != null) { // Check for null or undefined
            params = params.append('endDateNumber', endDateNumber.toString());
        }
        return this.http.get<ProfitAndLoss[]>(`${this.apiBaseUrl}/profit-and-loss/${owningCompanyId}`, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    public getProfitAndLossV2(
        owningCompanyId: number,
        selectedPeriodType: string,
        startDateNumber?: number | null,
        endDateNumber?: number | null,
        logisticsProjectId?: number | null,
    ): Observable<ReportResponse<ProfitAndLoss>> {
        let params = new HttpParams().append('selectedPeriodType', selectedPeriodType);
        if (startDateNumber != null) params = params.append('startDateNumber', startDateNumber.toString());
        if (endDateNumber != null) params = params.append('endDateNumber', endDateNumber.toString());
        if (logisticsProjectId != null) params = params.append('logisticsProjectId', logisticsProjectId.toString());
        return this.http.get<ReportResponse<ProfitAndLoss>>(`${this.apiBaseUrl}/v2/profit-and-loss/${owningCompanyId}`, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the aged partner balance for a specific company.
     * @param owningCompanyId ID of the owning company
     */
    public getAgedPartnerBalance(owningCompanyId: number): Observable<AgedPartnerBalance[]> {
        return this.http.get<AgedPartnerBalance[]>(`${this.apiBaseUrl}/aged-partner-balance/${owningCompanyId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Retrieves the aged receivable report for a specific company.
     * @param owningCompanyId ID of the owning company
     * @param endDateNumber
     */
    public getAgedReceivable(owningCompanyId: number, // Changed from Long to number, made optional
                             endDateNumber?: number | null): Observable<AgedReceivable[]> {
        let params = new HttpParams();
        if (endDateNumber != null) { // Check for null or undefined
            params = params.append('endDateNumber', endDateNumber.toString());
        }
        return this.http.get<AgedReceivable[]>(`${this.apiBaseUrl}/aged-receivable/${owningCompanyId}`, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    public getAgedReceivableV2(owningCompanyId: number, endDateNumber?: number | null,
                               logisticsProjectId?: number | null, intervalDays = 30,
                               intervalCount = 4, agingBasis = 'DUE_DATE'): Observable<ReportResponse<AgedReceivable>> {
        let params = new HttpParams();
        if (endDateNumber != null) params = params.append('endDateNumber', endDateNumber.toString());
        if (logisticsProjectId != null) params = params.append('logisticsProjectId', logisticsProjectId.toString());
        params = params.append('intervalDays', intervalDays.toString());
        params = params.append('intervalCount', intervalCount.toString());
        params = params.append('agingBasis', agingBasis);
        return this.http.get<ReportResponse<AgedReceivable>>(`${this.apiBaseUrl}/v2/aged-receivable/${owningCompanyId}`, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the aged payable report for a specific company.
     * @param owningCompanyId ID of the owning company
     */
    public getAgedPayable(owningCompanyId: number): Observable<AgedPayable[]> {
        return this.http.get<AgedPayable[]>(`${this.apiBaseUrl}/aged-payable/${owningCompanyId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    public getAgedPayableV2(owningCompanyId: number, endDateNumber?: number | null,
                            logisticsProjectId?: number | null, intervalDays = 30,
                            intervalCount = 4, agingBasis = 'DUE_DATE'): Observable<ReportResponse<AgedPayable>> {
        let params = new HttpParams();
        if (endDateNumber != null) params = params.append('endDateNumber', endDateNumber.toString());
        if (logisticsProjectId != null) params = params.append('logisticsProjectId', logisticsProjectId.toString());
        params = params.append('intervalDays', intervalDays.toString());
        params = params.append('intervalCount', intervalCount.toString());
        params = params.append('agingBasis', agingBasis);
        return this.http.get<ReportResponse<AgedPayable>>(`${this.apiBaseUrl}/v2/aged-payable/${owningCompanyId}`, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the trial balance for a specific company.
     * @param owningCompanyId ID of the owning company
     * @param startDateNumber
     * @param endDateNumber
     */
    public getTrialBalance(owningCompanyId: number,
                           startDateNumber: number,
                           endDateNumber: number): Observable<TrialBalance[]> {
        let params = new HttpParams();
        if (startDateNumber != null) { // Check for null or undefined
            params = params.append('startDateNumber', startDateNumber.toString());
        }
        if (endDateNumber != null) { // Check for null or undefined
            params = params.append('endDateNumber', endDateNumber.toString());
        }

        return this.http.get<TrialBalance[]>(`${this.apiBaseUrl}/trial-balance/${owningCompanyId}`, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    public getTrialBalanceV2(owningCompanyId: number,
                             startDateNumber: number,
                             endDateNumber: number,
                             logisticsProjectId?: number | null): Observable<ReportResponse<TrialBalance>> {
        let params = new HttpParams();
        if (startDateNumber != null) params = params.append('startDateNumber', startDateNumber.toString());
        if (endDateNumber != null) params = params.append('endDateNumber', endDateNumber.toString());
        if (logisticsProjectId != null) params = params.append('logisticsProjectId', logisticsProjectId.toString());
        return this.http.get<ReportResponse<TrialBalance>>(`${this.apiBaseUrl}/v2/trial-balance/${owningCompanyId}`, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the tax report for a specific company.
     * @param owningCompanyId ID of the owning company
     * @param startDateNumber
     * @param endDateNumber
     */
    public getTaxReport(owningCompanyId: number,
                        startDateNumber: number,
                        endDateNumber: number): Observable<TaxReport[]> {
        let params = new HttpParams();
        if (startDateNumber != null) { // Check for null or undefined
            params = params.append('startDateNumber', startDateNumber.toString());
        }
        if (endDateNumber != null) { // Check for null or undefined
            params = params.append('endDateNumber', endDateNumber.toString());
        }
        return this.http.get<TaxReport[]>(`${this.apiBaseUrl}/tax-report/${owningCompanyId}`, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    public getTaxReportV2(owningCompanyId: number,
                          startDateNumber: number,
                          endDateNumber: number,
                          logisticsProjectId?: number | null): Observable<ReportResponse<TaxReport>> {
        let params = new HttpParams();
        if (startDateNumber != null) params = params.append('startDateNumber', startDateNumber.toString());
        if (endDateNumber != null) params = params.append('endDateNumber', endDateNumber.toString());
        if (logisticsProjectId != null) params = params.append('logisticsProjectId', logisticsProjectId.toString());
        return this.http.get<ReportResponse<TaxReport>>(`${this.apiBaseUrl}/v2/tax-report/${owningCompanyId}`, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves the employee salary payable report for a specific company and year.
     * @param owningCompanyId The ID of the owning company.
     * @param year The year for the report.
     * @param customerId Optional. Filters the report for a specific employee.
     * @param month Optional. Filters the report for a specific month (1-12).
     */
    public getEmployeeSalaryPayable(
        owningCompanyId: number,
        year: number,
        customerId?: number | null,
        month?: number | null
    ): Observable<EmployeeSalaryPayable[]> {
        let params = new HttpParams();

        if (customerId != null) {
            params = params.append('customerId', customerId.toString());
        }
        if (month != null) {
            params = params.append('month', month.toString());
        }

        const url = `${this.apiBaseUrl}/employee-salary-payable/${owningCompanyId}/${year}`;

        return this.http.get<EmployeeSalaryPayable[]>(url, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Retrieves the cash book for a specific company.
     * @param owningCompanyId ID of the owning company
     */
    public getCashBook(owningCompanyId: number): Observable<CashBook[]> {
        return this.http.get<CashBook[]>(`${this.apiBaseUrl}/cash-book/${owningCompanyId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Retrieves the day book for a specific company.
     * @param owningCompanyId ID of the owning company
     */
    public getDayBook(owningCompanyId: number): Observable<DayBook[]> {
        return this.http.get<DayBook[]>(`${this.apiBaseUrl}/day-book/${owningCompanyId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    public getStandardReport(
        reportCode: string,
        owningCompanyId: number,
        filter: Record<string, unknown>
    ): Observable<StandardReportResponse> {
        return this.http.post<StandardReportResponse>(
            `${this.apiBaseUrl}/v2/standard/${reportCode}/${owningCompanyId}`,
            filter
        ).pipe(catchError(this.handleError));
    }

    /** Returns the authoritative, server-rendered Finance report PDF. */
    public printFinanceReport(
        reportCode: string,
        owningCompanyId: number,
        filter: FinancialReportPrintRequest
    ): Observable<Blob> {
        return this.http.post(
            `${this.apiBaseUrl}/v2/print/${reportCode}/${owningCompanyId}`,
            filter,
            { responseType: 'blob' }
        ).pipe(catchError(this.handleError));
    }

    // Basic error handler
    private handleError(error: any): Observable<never> {
        console.error('An API error occurred:', error);
        // You could transform the error into a user-friendly message
        // or re-throw it for a component-level handler
        return throwError(() => new Error('Something bad happened; please try again later.'));
    }
}
